import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { freePublishingState } from '../../lib/freePublishing.js';
import { freeOnboardingDestination, FREE_ONBOARDING_PATH } from '../../lib/freeOnboarding.js';
import { freePreviewClientEnabled } from '../../lib/flags.js';

export default function FreePublishingOnboardingGate({ children }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dash = useDashboardData();
  const location = useLocation();
  const [settledFor, setSettledFor] = useState(null);
  const [readScope, setReadScope] = useState(null);
  useEffect(() => {
    if (dash.entitlements && !dash.entitlementsError) {
      setReadScope(dash.captureEntitlementsScope?.() || null);
    }
  }, [dash.entitlements, dash.entitlementsError, dash.captureEntitlementsScope]);
  // Continuity only after a current snapshot was observed in this identity's
  // lifecycle. The provider still withholds stale mutation access; the server
  // admits every AI request. Always evaluate the latest authoritative snapshot.
  const entitlements = dash.entitlements
    || (dash.isEntitlementsScopeCurrent?.(readScope) ? dash.readEntitlements : null);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(dash.ensureDashboardData?.({ keys: ['entitlements'] })).catch(() => {}).finally(() => {
      if (!cancelled) setSettledFor(user?.id);
    });
    return () => { cancelled = true; };
  }, [user?.id, dash.ensureDashboardData]);
  if (dash.entitlementsError && !entitlements) return <Navigate to="/app" replace />;
  if (!entitlements) {
    if (settledFor !== user?.id) return <p role="status">{t('settings.checkingAccess')}</p>;
    return <Navigate to="/app" replace />;
  }
  const capability = freePublishingState(entitlements);
  const explicit = new URLSearchParams(location.search).get('source') === 'free-preview';
  if (capability || entitlements.accessState === 'free' || (explicit && entitlements.accessState === 'new')) {
    const destination = freePreviewClientEnabled() && explicit && freeOnboardingDestination(entitlements);
    if (destination !== FREE_ONBOARDING_PATH) return <Navigate to={destination || '/app'} replace />;
  }
  return children;
}
